import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  TrendingUp as ProfitIcon,
  TrendingDown as LossIcon,
  AttachMoney as RevenueIcon,
  Receipt as ExpenseIcon
} from '@mui/icons-material';

import { exportToPDF, exportToExcel, exportToCSV } from '../../utils/exportUtils';

// Types
interface ProfitLossData {
  period_start: string;
  period_end: string;
  revenue: {
    sales_revenue: number;
    other_income: number;
    total_revenue: number;
    sales_revenue_formatted: string;
    other_income_formatted: string;
    total_revenue_formatted: string;
  };
  expenses: {
    cost_of_sales: number;
    operating_expenses: number;
    administrative_expenses: number;
    total_expenses: number;
    cost_of_sales_formatted: string;
    operating_expenses_formatted: string;
    administrative_expenses_formatted: string;
    total_expenses_formatted: string;
  };
  gross_profit: number;
  net_profit: number;
  gross_profit_formatted: string;
  net_profit_formatted: string;
  gross_margin: number;
  net_margin: number;
  vat_summary: {
    vat_on_sales: number;
    vat_on_purchases: number;
    net_vat_position: number;
    vat_on_sales_formatted: string;
    vat_on_purchases_formatted: string;
    net_vat_position_formatted: string;
  };
}

interface ProfitLossReportProps {
  startDate: Date;
  endDate: Date;
  loading: boolean;
  onExport: (format: string) => void;
}

const ProfitLossReport: React.FC<ProfitLossReportProps> = ({
  startDate,
  endDate,
  loading,
  onExport
}) => {
  const [reportData, setReportData] = useState<ProfitLossData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfitLossData();
  }, [startDate, endDate]);

  const fetchProfitLossData = async () => {
    try {
      setError(null);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const response = await fetch(
        `/api/v1/reports/advanced/profit-loss?start_date=${startDateStr}&end_date=${endDateStr}`
      );

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      } else {
        throw new Error('Failed to fetch profit & loss data');
      }
    } catch (error) {
      console.error('Error fetching P&L data:', error);
      setError('Failed to load profit & loss report');
    }
  };

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    if (!reportData) {
      console.error('No report data available for export');
      return;
    }

    const reportInfo = {
      title: 'Profit & Loss Report',
      period: `${startDate.toLocaleDateString('en-GB')} to ${endDate.toLocaleDateString('en-GB')}`,
      data: reportData,
      type: 'profit-loss' as const
    };

    try {
      switch (format) {
        case 'pdf':
          exportToPDF(reportInfo);
          break;
        case 'excel':
          exportToExcel(reportInfo);
          break;
        case 'csv':
          exportToCSV(reportInfo);
          break;
      }

      // Call the parent onExport for UI feedback
      if (onExport) {
        onExport(format);
      }
    } catch (error) {
      console.error('Export failed:', error);
    }
  };



  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Loading Profit & Loss Report...</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="info">
        No data available for the selected period.
      </Alert>
    );
  }

  const formatTableRow = (label: string, amount: string, isTotal = false, isSubtotal = false) => (
    <TableRow>
      <TableCell sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit',
        pl: isSubtotal ? 2 : isTotal ? 0 : 3
      }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{
        fontWeight: isTotal ? 'bold' : isSubtotal ? 'medium' : 'normal',
        fontSize: isTotal ? '1.1rem' : 'inherit'
      }}>
        {amount}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Report Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Profit & Loss Statement
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {new Date(reportData.period_start).toLocaleDateString('en-GB')} to {new Date(reportData.period_end).toLocaleDateString('en-GB')}
          </Typography>
        </Box>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <RevenueIcon color="primary" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                  <Typography variant="h6">{reportData.revenue.total_revenue_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ExpenseIcon color="warning" />
                <Box>
                  <Typography variant="body2" color="textSecondary">Total Expenses</Typography>
                  <Typography variant="h6">{reportData.expenses.total_expenses_formatted}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                {reportData.gross_profit >= 0 ? <ProfitIcon color="success" /> : <LossIcon color="error" />}
                <Box>
                  <Typography variant="body2" color="textSecondary">Gross Profit</Typography>
                  <Typography variant="h6" color={reportData.gross_profit >= 0 ? 'success.main' : 'error.main'}>
                    {reportData.gross_profit_formatted}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {reportData.gross_margin.toFixed(1)}% margin
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                {reportData.net_profit >= 0 ? <ProfitIcon color="success" /> : <LossIcon color="error" />}
                <Box>
                  <Typography variant="body2" color="textSecondary">Net Profit</Typography>
                  <Typography variant="h6" color={reportData.net_profit >= 0 ? 'success.main' : 'error.main'}>
                    {reportData.net_profit_formatted}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {reportData.net_margin.toFixed(1)}% margin
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed P&L Statement */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Account</strong></TableCell>
              <TableCell align="right"><strong>Amount</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Revenue Section */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', fontWeight: 'bold' }}>
                REVENUE
              </TableCell>
            </TableRow>
            {formatTableRow('Sales Revenue', reportData.revenue.sales_revenue_formatted)}
            {formatTableRow('Other Income', reportData.revenue.other_income_formatted)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Revenue', reportData.revenue.total_revenue_formatted, false, true)}

            {/* Cost of Sales */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 'bold', pt: 2 }}>
                COST OF SALES
              </TableCell>
            </TableRow>
            {formatTableRow('Cost of Sales', reportData.expenses.cost_of_sales_formatted)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Gross Profit', reportData.gross_profit_formatted, false, true)}

            {/* Operating Expenses */}
            <TableRow>
              <TableCell colSpan={2} sx={{ bgcolor: 'error.light', color: 'error.contrastText', fontWeight: 'bold', pt: 2 }}>
                OPERATING EXPENSES
              </TableCell>
            </TableRow>
            {formatTableRow('Operating Expenses', reportData.expenses.operating_expenses_formatted)}
            {formatTableRow('Administrative Expenses', reportData.expenses.administrative_expenses_formatted)}
            <TableRow>
              <TableCell colSpan={2}><Divider /></TableCell>
            </TableRow>
            {formatTableRow('Total Expenses', reportData.expenses.total_expenses_formatted, false, true)}

            {/* Net Profit */}
            <TableRow>
              <TableCell colSpan={2} sx={{ pt: 2 }}><Divider sx={{ borderWidth: 2 }} /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: reportData.net_profit >= 0 ? 'success.light' : 'error.light' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                NET {reportData.net_profit >= 0 ? 'PROFIT' : 'LOSS'}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                {reportData.net_profit_formatted}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* VAT Summary */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            VAT Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="textSecondary">VAT on Sales</Typography>
                <Typography variant="h6">{reportData.vat_summary.vat_on_sales_formatted}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="textSecondary">VAT on Purchases</Typography>
                <Typography variant="h6">{reportData.vat_summary.vat_on_purchases_formatted}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="body2" color="textSecondary">Net VAT Position</Typography>
                <Typography variant="h6" color={reportData.vat_summary.net_vat_position >= 0 ? 'error.main' : 'success.main'}>
                  {reportData.vat_summary.net_vat_position_formatted}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {reportData.vat_summary.net_vat_position >= 0 ? 'Due to HMRC' : 'Refund from HMRC'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfitLossReport;
